// shared/src/oracle/relations-dash-bundle.ts
// The /oracle "Relations" tab read contract (P2.5 slice 5.2). A wrapped-token DEPEG
// monitor: each CURRENTLY-WATCHED wrapped key (enabled oracle_relations_config rows —
// $WBTC/$WORMSOL today) vs its native reference, the fixed peg ratio, the observed ratio
// (key_value/native_value), the signed deviation vs its tolerance, and a deviation sparkline
// over the read window; plus a discrete breach event log. (A retired relation — e.g. $WSTETH,
// #66 — must not ghost: the breach log filters to input.currentRelationKeys, `latest` already
// self-drops via latest-run scoping.)
//
// A SEPARATE bundle + endpoint from the price/macro ones (mirrors macro-dash-bundle.ts):
// its shape is a peg series, not a resolve. Defined ONCE here so the api-worker projector
// and the apps/web tab share it verbatim (no C035 hand-mirror). The projector runs the
// SQL (readLatestRelations / readRelationSeries / readRecentBreaches); this PURE assembler
// shapes it — no I/O.

import { z } from 'zod';
import type { OracleTokenRelationRow } from './relations-store.js';

// ─────────────────────────── view schemas ───────────────────────────

/** One point on a wrapped token's deviation drift chart. */
export const RelationDeviationPointSchema = z.object({
  created_at:    z.string(),
  deviation_pct: z.number(),   // signed fraction (observed/expected − 1)
  breached:      z.boolean(),
});

/** One wrapped token's CURRENT peg state + its deviation series over the window. */
export const OracleRelationRowSchema = z.object({
  key:            z.string(),          // wrapped key ('$WSTETH')
  native_key:     z.string(),          // native reference ('$ETH')
  key_value:      z.number().nullable(),
  native_value:   z.number().nullable(),
  expected_ratio: z.number(),
  observed_ratio: z.number(),
  deviation_pct:  z.number(),          // signed fraction
  tol:            z.number(),
  breached:       z.boolean(),
  ratio_source:   z.string(),          // 'fixed:1' | 'lido:stEthPerToken' | …
  observed_at:    z.string().nullable(),
  created_at:     z.string(),
  age_s:          z.number().nullable(), // now − (observed_at ?? created_at)
  sparkline:      z.array(RelationDeviationPointSchema), // oldest→newest deviation over the window
});

/** One historical breach event (breached=1), for the discrete audit log. */
export const OracleBreachRowSchema = z.object({
  key:            z.string(),
  native_key:     z.string(),
  deviation_pct:  z.number(),
  tol:            z.number(),
  observed_ratio: z.number(),
  expected_ratio: z.number(),
  ratio_source:   z.string(),
  created_at:     z.string(),
});

export const OracleRelationsBundleSchema = z.object({
  generated_at: z.string(),
  window_days:  z.number(),
  rows:         z.array(OracleRelationRowSchema),   // one per wrapped token (current peg + drift)
  breaches:     z.array(OracleBreachRowSchema),     // the breach event log (newest first, capped)
});

export type RelationDeviationPoint  = z.infer<typeof RelationDeviationPointSchema>;
export type OracleRelationRow       = z.infer<typeof OracleRelationRowSchema>;
export type OracleBreachRow         = z.infer<typeof OracleBreachRowSchema>;
export type OracleRelationsBundle   = z.infer<typeof OracleRelationsBundleSchema>;

// ─────────────────────────── pure assembler ───────────────────────────

export interface AssembleRelationsInput {
  readonly nowMs:       number;
  readonly generatedAt: string;   // ISO-Z
  readonly windowDays:  number;
  /** Latest row per wrapped key — the current peg state. */
  readonly latest:      ReadonlyArray<OracleTokenRelationRow>;
  /** Each key's deviation series over the window (oldest→newest), keyed by wrapped key. */
  readonly seriesByKey: ReadonlyMap<string, ReadonlyArray<OracleTokenRelationRow>>;
  /** The breach event log (already newest-first, capped). */
  readonly breaches:    ReadonlyArray<OracleTokenRelationRow>;
  /** The CURRENTLY-WATCHED wrapped keys (enabled rows of oracle_relations_config). The breach log
   *  filters to this so a retired relation's stale breaches don't ghost (#93). The projector reads
   *  it from the config table; a retirement drops the key here and its old rows vanish next load. */
  readonly currentRelationKeys: ReadonlySet<string>;
}

function ageSeconds(at: string | null, nowMs: number): number | null {
  if (!at) return null;
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? Math.round((nowMs - ms) / 1000) : null;
}

/** PURE: already-fetched relation rows → the validated Relations bundle. No I/O. */
export function assembleOracleRelationsBundle(input: AssembleRelationsInput): OracleRelationsBundle {
  const { nowMs } = input;

  const rows: OracleRelationRow[] = input.latest
    .slice()
    .sort((a, b) => a.key.localeCompare(b.key))
    .map((r) => ({
      key:            r.key,
      native_key:     r.native_key,
      key_value:      r.key_value,
      native_value:   r.native_value,
      expected_ratio: r.expected_ratio,
      observed_ratio: r.observed_ratio,
      deviation_pct:  r.deviation_pct,
      tol:            r.tol,
      breached:       r.breached === 1,
      ratio_source:   r.ratio_source,
      observed_at:    r.observed_at,
      created_at:     r.created_at,
      age_s:          ageSeconds(r.observed_at ?? r.created_at, nowMs),
      sparkline:      (input.seriesByKey.get(r.key) ?? []).map((p) => ({
        created_at:    p.created_at,
        deviation_pct: p.deviation_pct,
        breached:      p.breached === 1,
      })),
    }));

  // Only breaches for CURRENTLY-WATCHED relations — a retired relation ($WSTETH, #66) leaves
  // historical breached=1 rows that readRecentBreaches (no window, no key filter) would surface
  // forever; worse, those were FALSE positives (the retirement reason). currentRelationKeys is the
  // live watched set from oracle_relations_config, so this self-heals for any future retirement.
  const breaches: OracleBreachRow[] = input.breaches
    .filter((b) => input.currentRelationKeys.has(b.key))
    .map((b) => ({
    key:            b.key,
    native_key:     b.native_key,
    deviation_pct:  b.deviation_pct,
    tol:            b.tol,
    observed_ratio: b.observed_ratio,
    expected_ratio: b.expected_ratio,
    ratio_source:   b.ratio_source,
    created_at:     b.created_at,
  }));

  return {
    generated_at: input.generatedAt,
    window_days:  input.windowDays,
    rows,
    breaches,
  };
}
